import { Container, Loader, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation } from 'react-router-dom';

import { DefaultWidgetNames, WidgetType } from '@/api/rest/resources/types/widget';
import { SecondaryNav, SuspenseBoundary, TabsList, TabTrigger } from '@/components';
import { MapRadiusContextProvider } from '@/components/MapOverview/components/MapRadiusContextProvider';
import { useMapLayers } from '@/components/widgets/o/Map/hooks/useMapLayers';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { usePathSet } from '@/hooks/usePathSet';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { useMenuItems } from '@/pages/shared/hooks/useMenuItems';
import { usePlot } from '@/pages/shared/hooks/usePlot';
import { buildPath } from '@/utils/buildPath';

import { PlotActionsDropdown } from './components/PlotActionsDropdown';
import { PlotMap } from './components/PlotMap';
import { LandscapeViewContextProvider } from './LandscapeViewContextProvider';
import { LandMonitoringPage } from './pages/LandMonitoring.page';
import { PlotOverviewPage } from './pages/PlotOverview.page';

export const TabPlotView = () => {
  const { t } = useTranslation();
  const pathSet = usePathSet();

  const plot = usePlot().data;
  const widgetSlotsConfiguration = useActiveWidgetSlotConfiguration(
    DefaultWidgetNames.default_template_plot_detail,
    WidgetType.t_page,
  );

  const location = useLocation();

  const overviewPath = buildPath(pathSet.plotOverview, { pathParams: { plotId: plot.id } });

  const landMonitoringPath = buildPath(pathSet.plotLandMonitoring, { pathParams: { plotId: plot.id } });

  const menuItems = useMenuItems();

  const mapLayers = useMapLayers();

  return (
    <MapRadiusContextProvider>
      <LandscapeViewContextProvider>
        <SignedInPageLayout items={menuItems} withCustomContentContainer>
          <SecondaryNav
            title={plot.name}
            backPath={buildPath(pathSet.plots, {
              queryParams: { page: location.state?.plotPage },
            })}
            action={
              <Stack className='grow items-end'>
                <PlotActionsDropdown />
              </Stack>
            }
            className='justify-start !gap-12'
          />

          <Container gutterWidth={{ sm: 4 }} className='gap-10' data-testid='tab-plot-view'>
            <SuspenseBoundary
              fallback={
                <Stack center className='h-[600px]'>
                  <Loader />
                </Stack>
              }
            >
              <div className='h-[60vh] animate-in fade-in'>
                <PlotMap plot={plot} layers={mapLayers} />
              </div>
            </SuspenseBoundary>
            <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />

            <TabsList className='my-10 justify-start sm:justify-center rounded-2xl bg-neutral-black-4 w-full'>
              <TabTrigger path={overviewPath}>{t('shared.projects.labels.overview')}</TabTrigger>
              <TabTrigger path={landMonitoringPath}>{t('shared.projects.labels.landMonitoring')}</TabTrigger>
            </TabsList>
            <SuspenseBoundary
              fallback={
                <Stack center className='h-[600px]'>
                  <Loader />
                </Stack>
              }
            >
              <Routes>
                <Route index element={<PlotOverviewPage />} />
                <Route path={pathSet.plotOverview.split('/').pop()} element={<PlotOverviewPage />} />
                <Route path={pathSet.plotLandMonitoring.split('/').pop()} element={<LandMonitoringPage />} />
              </Routes>
            </SuspenseBoundary>
          </Container>
        </SignedInPageLayout>
      </LandscapeViewContextProvider>
    </MapRadiusContextProvider>
  );
};
